/**
 * Componente Tooltip.
 *
 * Componente de tooltip que sirve para mostrar información adicional sobre alguna herramienta,
 * que además recibe estilos adicionales opcionales
 * tanto para el propio Tooltip (tooltipSx) como para el ícono (iconSx).
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-04-08]
 * @updated [Fecha de última modificación: 2025-04-08]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import React, { useState } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

/**
 * Componente de tooltip que recibe estilos adicionales opcionales
 * tanto para el propio Tooltip (tooltipSx) como para el ícono (iconSx).
 *
 * @param {string} message - Mensaje mostrado dentro del Tooltip.
 * @param {Object} tooltipSx - (Opcional) Estilos adicionales para el Tooltip.
 * @param {Object} iconSx - (Opcional) Estilos adicionales para el ícono HelpOutline.
 * @returns {JSX.Element} Componente Tooltip.
 */
export default function ToolTip({ message, tooltipSx = {}, iconSx = {} }) {
  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  }));

  // Estilos predeterminados del ícono
  const defaultIconSx = {
    ml: 0,
    fontSize: 18,
    color: "info.main",
    cursor: "pointer",
  };

  return (
    <>
      <HtmlTooltip sx={tooltipSx} title={<React.Fragment>{message}</React.Fragment>}>
        <HelpOutlineIcon sx={{ ...defaultIconSx, ...iconSx }} />
      </HtmlTooltip>
    </>
  );
}
